/**
 * ScopeToggle — a small "Mine | All" segmented control used on the league-scoped
 * browsers (Games, Teams) so a signed-in player can default to their own items
 * and expand to everything in the division/league with one click.
 *
 * Stateless: the parent owns the `scope` value. Only render this when the viewer
 * is authenticated — there is no "mine" for logged-out visitors.
 */
import React from 'react';

export type Scope = 'mine' | 'all';

interface ScopeToggleProps {
  scope: Scope;
  onChange: (scope: Scope) => void;
  mineLabel?: string;
  allLabel?: string;
  className?: string;
  /** Accessible label for the button group. */
  ariaLabel?: string;
}

const ScopeToggle: React.FC<ScopeToggleProps> = ({
  scope,
  onChange,
  mineLabel = 'Mine',
  allLabel = 'All',
  className = '',
  ariaLabel = 'Show mine or all',
}) => {
  const base =
    'px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500';
  const active = 'bg-primary-600 text-white';
  const inactive = 'bg-white text-gray-600 hover:bg-gray-50';

  return (
    <div
      className={`inline-flex rounded-md border border-gray-300 overflow-hidden ${className}`}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => onChange('mine')}
        aria-pressed={scope === 'mine'}
        className={`${base} ${scope === 'mine' ? active : inactive}`}
      >
        {mineLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange('all')}
        aria-pressed={scope === 'all'}
        className={`${base} border-l border-gray-300 ${scope === 'all' ? active : inactive}`}
      >
        {allLabel}
      </button>
    </div>
  );
};

export default ScopeToggle;
